import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2, Sparkles, Zap, Building2 } from "lucide-react";
import { PortalShell } from "../components/layout/PortalShell";
import { useAuth } from "../context/AuthContext";
import { apiBillingPlans, apiBillingMe, apiBillingCheckout, apiBillingStatus } from "../lib/api";

const ICONS = { free: Sparkles, professional: Zap, enterprise: Building2 };

export default function Billing() {
  const { setUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState("");
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const loadMe = useCallback(async () => { try { setMe(await apiBillingMe()); } catch { /* */ } }, []);

  useEffect(() => { apiBillingPlans().then(setPlans).catch(() => {}); loadMe(); }, [loadMe]);

  // Handle Stripe return
  useEffect(() => {
    const sessionId = params.get("session_id");
    const cancelled = params.get("cancelled");
    if (cancelled) { toast.info("Checkout cancelled."); setParams({}, { replace: true }); return; }
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await apiBillingStatus(sessionId);
        if (res.payment_status === "paid") {
          toast.success("Payment successful — your plan is now active!");
          await loadMe();
          try { const u = await (await import("../lib/api")).apiMe(); setUser(u); } catch { /* */ }
          setParams({}, { replace: true });
          return;
        }
        if (res.status === "expired") { toast.error("Checkout session expired."); setParams({}, { replace: true }); return; }
      } catch { /* */ }
      attempts += 1;
      if (attempts < 6) setTimeout(poll, 2000);
      else setParams({}, { replace: true });
    };
    toast.loading("Confirming payment…", { id: "pay", duration: 2000 });
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upgrade = async (planId) => {
    setBusy(planId);
    try {
      const { url } = await apiBillingCheckout(planId);
      window.location.href = url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout.");
      setBusy("");
    }
  };

  const pct = me ? Math.min(100, Math.round((me.used / me.limit) * 100)) : 0;

  return (
    <PortalShell>
      <div className="mb-8">
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-navy">Plans & Billing</h1>
        <p className="mt-1 text-muted-foreground">Choose the plan that fits your compliance workload.</p>
      </div>

      {me && (
        <div className="mb-8 rounded-2xl border border-border bg-white p-6" data-testid="usage-panel">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-sm text-muted-foreground">Current plan</div>
              <div className="font-display text-xl font-extrabold text-navy">{me.plan_name}</div>
            </div>
            <div className="min-w-[240px] flex-1">
              <div className="mb-1 flex justify-between text-xs text-muted-foreground">
                <span>AI generations this month</span><span>{me.used} / {me.limit}</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-secondary">
                <div className="h-full rounded-full bg-aero transition-all" style={{ width: `${pct}%` }} />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {plans.map((p) => {
          const Icon = ICONS[p.id] || Sparkles;
          const current = me?.plan === p.id;
          const highlight = p.id === "professional";
          return (
            <div key={p.id} data-testid={`plan-${p.id}`} className={`relative flex flex-col rounded-2xl border bg-white p-8 ${highlight ? "border-aero shadow-lg" : "border-border"}`}>
              {highlight && <span className="absolute -top-3 left-8 rounded-full bg-aero px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white">Most popular</span>}
              <span className={`inline-flex h-12 w-12 items-center justify-center rounded-xl ${highlight ? "bg-aero/10 text-aero" : "bg-brand/10 text-brand"}`}>
                <Icon className="h-6 w-6" />
              </span>
              <h3 className="mt-5 font-display text-xl font-bold text-navy">{p.name}</h3>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="font-display text-4xl font-extrabold text-navy">${p.price % 1 === 0 ? p.price : p.price.toFixed(2)}</span>
                <span className="text-sm text-muted-foreground">/ month</span>
              </div>
              <ul className="mt-6 flex-1 space-y-3">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-navy">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" /> {f}
                  </li>
                ))}
              </ul>
              <button
                disabled={current || p.id === "free" || busy === p.id}
                onClick={() => upgrade(p.id)}
                data-testid={`upgrade-${p.id}`}
                className={`mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-transform ${
                  current ? "cursor-default bg-secondary text-muted-foreground"
                  : p.id === "free" ? "cursor-default bg-secondary text-muted-foreground"
                  : "bg-aero text-white hover:-translate-y-0.5"
                }`}
              >
                {busy === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {current ? "Current plan" : p.id === "free" ? "Free forever" : `Upgrade to ${p.name}`}
              </button>
            </div>
          );
        })}
      </div>

      <p className="mt-8 text-center text-xs text-muted-foreground">
        Secure checkout by Stripe · Test mode. Prices are indicative and can be adjusted anytime.
      </p>
    </PortalShell>
  );
}
